import React from "react";
import { Observer } from "mobx-react";
import moment from "moment";
import { TimereportStatus, TimereportTrackingDto } from "@api/timereports";
import { IconButton, Step, StepLabel, Stepper, Typography } from "@material-ui/core";
import { Replay } from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import { WithTooltip } from "@common";
import FeedbackIcon from "@material-ui/icons/FeedbackOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

/**
 * Recipients the last send-to-customer attempt never reached (CRMDEV-7141).
 * The tracking DTO comes from timereport-api and only knows about sends that
 * succeeded, so the failures are fetched separately and merged in here.
 */
export type SendFailures = {
  partiallySent: boolean;
  failures: Array<{
    recipient: string;
    failedOn: Date | string;
    reason?: string | null;
  }>;
};

export type Props = {
  statusHistory: TimereportTrackingDto;
  sendFailures?: SendFailures;
  setToDraft: () => void;
  disabledSetToDraft: boolean;
  isSendToConsultant: boolean;
};

export const TimeReportStatus = (props: Props) => {
  if (!props.statusHistory) {
    return null;
  }

  const history = [
    getDraftStep(props.statusHistory),
    getSubmitByConsultantStep(props.statusHistory),
    getSentToConsultantCustomerStep(
      props.statusHistory,
      props.isSendToConsultant,
      props.sendFailures
    ),
    getReviewedByCustomerStep(props.statusHistory),
    getPaymentProcessedStep(props.statusHistory)
  ];

  const activeIndex = history.findIndex((x) => x.isActive);

  return (
    <Observer>
      {() => (
        <Stepper
          // alternativeLabel
          style={{ width: "100%", marginBottom: 5, padding: "8px 16px" }}
          activeStep={activeIndex}
        >
          {history.map((x, index) => {
            return (
              <Step key={index} completed={index <= activeIndex}>
                <StepLabel>
                  <div>
                    {x.label}{" "}
                    {x.comment?.length > 0 && (
                      <WithTooltip title={x.comment}>
                        <FeedbackIcon />
                      </WithTooltip>
                    )}
                  </div>

                  <div>
                    <Typography variant="caption">
                      {x.user ? x.user : x.time ? "Unknown" : ""}
                    </Typography>

                    {index === 0 && index <= activeIndex && (
                      <IconButton
                        disabled={props.disabledSetToDraft}
                        size={"small"}
                        onClick={props.setToDraft}
                        title="Revert to draft"
                      >
                        <Replay />
                      </IconButton>
                    )}
                  </div>

                  {x.time && (
                    <Typography variant="caption" display="block">
                      {x.time}
                    </Typography>
                  )}
                  {x.recipient && (
                    <Typography variant="caption" display="block">
                      {x.recipient}
                    </Typography>
                  )}
                  {x.reminders?.length > 0 && (
                    <>
                      <Typography variant="caption" display="block">
                        {locale.Reminder}:
                      </Typography>

                      {x.reminders?.map((r, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          display="block"
                          color={r.error ? "error" : "textPrimary"}
                        >
                          {r.to} {r.sentOn || r.sendOn}
                          {r.error && (
                            <Typography
                              color="error"
                              variant="caption"
                              title={r.error}
                              style={{ marginLeft: 8 }}
                              display="inline"
                            >
                              Error!
                            </Typography>
                          )}
                        </Typography>
                      ))}
                    </>
                  )}
                  {x.failures?.length > 0 && (
                    <>
                      <Typography variant="caption" display="block" color="error">
                        <ErrorOutlineIcon
                          style={{ fontSize: 14, verticalAlign: "text-bottom" }}
                        />{" "}
                        {x.failureLabel}
                      </Typography>

                      {x.failures.map((f, i) => (
                        <WithTooltip
                          key={i}
                          title={
                            f.reason || locale.timereportSendingToCustomer.stepNoReason
                          }
                        >
                          <Typography variant="caption" display="block" color="error">
                            {f.to} {f.failedOn && `(${f.failedOn})`}
                          </Typography>
                        </WithTooltip>
                      ))}
                    </>
                  )}
                  {x.comment?.length > 0 && (
                    <WithTooltip title={x.comment}>
                      <Typography
                        variant="caption"
                        display="block"
                        noWrap
                        style={{ width: "100px" }}
                      >
                        {x.comment}
                      </Typography>
                    </WithTooltip>
                  )}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      )}
    </Observer>
  );
};

function getDraftStep(dto: TimereportTrackingDto) {
  const draftOn = dto.setToDraftOnUtc || dto.createdOnUtc;
  return {
    user: dto.setToDraftBy?.name ?? dto.createdBy?.name,
    recipient: "",
    time: draftOn ? moment(draftOn).format(fmt) : "",
    label: dto.currentStatus === TimereportStatus.Blank ? "Tom" : "Utkast",
    reminders: [],
    failures: [],
    failureLabel: "",
    comment: "",
    isActive:
      dto.currentStatus === TimereportStatus.Draft ||
      dto.currentStatus === TimereportStatus.Blank
  };
}

function getSentToConsultantCustomerStep(
  dto: TimereportTrackingDto,
  isSendToConsultant = false,
  sendFailures?: SendFailures
) {
  if (isSendToConsultant) {
    return {
      user: dto.sentToConsultantBy?.name,
      comment: "",
      time: dto.sentToConsultantOnUtc
        ? moment(dto.sentToConsultantOnUtc).format(fmt)
        : "",
      reminders: [],
      failures: [],
      failureLabel: "",
      label: "Skickad till konsult",
      isActive: dto.currentStatus === TimereportStatus.SentToConsultant
    };
  }
  return {
    user: dto.sentToCustomerBy?.name,
    recipient: dto.sentToCustomerContactPerson,
    comment: "",
    time: dto.sentToCustomerOnUtc ? moment(dto.sentToCustomerOnUtc).format(fmt) : "",
    reminders:
      dto.sentReminders?.map((x) => ({
        to: x.sentTo,
        sentOn: x.sentOn ? `(skickades ${moment(x.sentOn).format(fmt)})` : "",
        sendOn: !x.sentOn
          ? x.scheduledOn
            ? `(kommande ${moment(x.scheduledOn).format(fmt)})`
            : "(kommande utskick)"
          : "",
        error: x.error || ""
      })) ?? [],
    failures:
      sendFailures?.failures?.map((x) => ({
        to: x.recipient,
        failedOn: x.failedOn ? moment(x.failedOn).format(fmt) : "",
        reason: x.reason || ""
      })) ?? [],
    // Nobody was reached, so the timereport is still where it was — say that,
    // rather than leaving a step that looks untouched (CRMDEV-7141).
    failureLabel: sendFailures?.partiallySent
      ? locale.timereportSendingToCustomer.stepNotReached
      : locale.timereportSendingToCustomer.stepSendFailed,
    label: "Skickad till kund",
    isActive: dto.currentStatus === TimereportStatus.SentToCustomer
  };
}

function getSubmitByConsultantStep(dto: TimereportTrackingDto) {
  return {
    user: dto.consultantSubmittedBy?.name,
    recipient: "",
    comment: "",
    time: dto.consultantSubmittedOnUtc
      ? moment(dto.consultantSubmittedOnUtc).format(fmt)
      : "",
    reminders: [],
    failures: [],
    failureLabel: "",
    label: "Inskickad",
    isActive: dto.currentStatus === TimereportStatus.SubmittedByConsultant
  };
}

function getReviewedByCustomerStep(dto: TimereportTrackingDto) {
  const isActive =
    dto.currentStatus === TimereportStatus.ApprovedByCustomer ||
    dto.currentStatus === TimereportStatus.RejectedByCustomer;

  if (
    (dto.rejectedByCustomerOnUtc &&
      dto.approvedByCustomerOnUtc &&
      moment(dto.rejectedByCustomerOnUtc).isAfter(dto.approvedByCustomerOnUtc)) ||
    (dto.rejectedByCustomerOnUtc && !dto.approvedByCustomerOnUtc)
  ) {
    return {
      user: dto.rejectedByCustomerContactPerson ?? "",
      recipient: "",
      reminders: [],
      failures: [],
      failureLabel: "",
      time: moment(dto.rejectedByCustomerOnUtc).format(fmt),
      label: "Nekad av kund",
      isActive,
      comment: dto.reviewByCustomerComment
    };
  }

  if (dto.approvedByCustomerOnUtc) {
    return {
      user: dto.approvedByCustomerContactPerson ?? "",
      reminders: [],
      failures: [],
      failureLabel: "",
      recipient: "",
      time: moment(dto.approvedByCustomerOnUtc).format(fmt),
      label: "Godkänd av kund",
      isActive,
      comment: dto.reviewByCustomerComment
    };
  }

  return { ...empty, label: "Godkänd av kund" };
}

function getPaymentProcessedStep(dto: TimereportTrackingDto) {
  return {
    user: dto.consultantPaymentProcessedBy?.name ?? "",
    reminders: [],
    failures: [],
    failureLabel: "",
    recipient: "",
    comment: "",
    time: dto.consultantPaymentProcessedOnUtc
      ? moment(dto.consultantPaymentProcessedOnUtc).format(fmt)
      : "",
    label: "Utbetald",
    isActive: dto.currentStatus === TimereportStatus.ConsultantPaymentProcessed
  };
}

const empty = {
  user: "",
  time: "",
  label: "",
  isActive: false,
  recipient: "",
  comment: "",
  reminders: [],
  failures: [],
  failureLabel: ""
};
const fmt = "DD/MM/YY HH:mm";
